/**
 * ChallengeProgressRing
 *
 * SVG circular progress ring for challenge participation. The ring is
 * intentionally gentle: the unfilled portion sits in a soft cream stroke
 * (never red, never alarming) and the filled arc uses the challenge tone.
 * The center shows "{daysLogged}/{target}" with the unit "days" beneath.
 *
 * No counts of other sisters' progress appear in or around this ring,
 * by design. Threshold-based, never leaderboard-based.
 */
export default function ChallengeProgressRing({
  daysLogged,
  targetDays,
  tone = "gold",
  size = 84,
  strokeWidth = 8,
}: {
  daysLogged: number;
  targetDays: number;
  tone?: "forest" | "coral" | "gold" | "purple" | "fiber";
  size?: number;
  strokeWidth?: number;
}) {
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, daysLogged / Math.max(1, targetDays)));
  const dash = c * pct;

  const color = colorFor(tone);

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      data-testid="ring-challenge-progress"
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(30,74,53,0.10)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dasharray 400ms ease-out" }}
        />
      </svg>
      <div
        className="absolute inset-0 flex flex-col items-center justify-center"
        aria-hidden
      >
        <span
          className="mnf-serif"
          style={{
            fontSize: size > 80 ? 22 : 18,
            fontWeight: 600,
            lineHeight: 1,
            color: "var(--mnf-fd)",
          }}
        >
          {daysLogged}
          <span style={{ color: "var(--mnf-tg)", fontWeight: 400 }}>
            {" "}/{targetDays}
          </span>
        </span>
        <span
          className="mnf-body"
          style={{
            fontSize: 9.5,
            color: "var(--mnf-tg)",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontWeight: 700,
            marginTop: 4,
          }}
        >
          days
        </span>
      </div>
    </div>
  );
}

function colorFor(tone: "forest" | "coral" | "gold" | "purple" | "fiber") {
  switch (tone) {
    case "coral":
      return "var(--mnf-t)";
    case "gold":
      return "var(--mnf-gold)";
    case "purple":
      return "var(--mnf-purple)";
    case "fiber":
      return "#2C6248";
    case "forest":
    default:
      return "var(--mnf-f)";
  }
}
